import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Lightbulb } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface Props {
  defaultName?: string;
  compact?: boolean;
  white?: boolean;
}

export const ClassSuggestionButton = ({ defaultName, compact, white }: Props) => {
  const { t } = useTranslation('student');
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [suggestion, setSuggestion] = useState('');

  const submit = async () => {
    if (suggestion.trim().length < 5) {
      toast({ title: t('suggest.error_short', 'Please describe your suggestion'), variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('submit-class-suggestion', {
        body: { name, email, suggestion },
      });
      if (error) throw error;
      toast({ title: t('suggest.success_title', 'Thanks!'), description: t('suggest.success_desc', 'Your suggestion was sent.') });
      setSuggestion('');
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast({ title: t('suggest.error_title', 'Something went wrong'), description: t('suggest.error_desc', 'Please try again later.'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={`gap-2 font-semibold whitespace-nowrap text-xs leading-tight h-auto py-2 ${white ? 'bg-white/80 text-primary border-white/80 hover:bg-white hover:text-primary shadow-sm' : 'border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary'} ${compact ? 'w-auto inline-flex px-3' : 'w-full justify-start text-left whitespace-normal'}`}
        >
          <Lightbulb className={`h-4 w-4 shrink-0 ${white ? 'text-primary' : ''}`} />
          {compact ? (
            <span>{t('suggest.button_compact', 'Suggest Episode')}</span>
          ) : (
            <>
              <span className="hidden sm:inline truncate">{t('suggest.button', 'Any suggestion? Classes you\u2019d like to see?')}</span>
              <span className="sm:hidden">{t('suggest.button_short', 'Suggest a class')}</span>
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('suggest.title', 'Suggest a new class')}</DialogTitle>
          <DialogDescription>
            {t('suggest.desc', 'Tell us what topics or classes you\u2019d like us to add. Your message goes straight to our team.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="cs-name">{t('suggest.name', 'Name')}</Label>
            <Input id="cs-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cs-email">{t('suggest.email', 'Email')}</Label>
            <Input id="cs-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cs-suggestion">{t('suggest.suggestion', 'Your suggestion')}</Label>
            <Textarea
              id="cs-suggestion"
              rows={5}
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              placeholder={t('suggest.placeholder', 'e.g. A class about real estate investing for beginners…')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
            {t('suggest.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? t('suggest.sending', 'Sending…') : t('suggest.send', 'Send suggestion')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};